import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, Zap, RotateCcw } from "lucide-react";
import { useMotion } from "./motion";

const TYPES = [
    "payment.succeeded",
    "invoice.created",
    "user.activated",
    "subscription.renewed",
    "order.fulfilled",
    "agent.task.completed",
    "device.telemetry",
    "payout.initiated",
];

const ENDPOINTS = [
    "api.acme.dev/hooks",
    "hooks.northwind.io/v2",
    "ingest.helios.app",
    "api.fern.co/events",
];

const rand = (arr) => arr[Math.floor(Math.random() * arr.length)];

const makeEvent = (n) => {
    const roll = Math.random();
    const status = roll < 0.84 ? 200 : roll < 0.94 ? 429 : 500;
    return {
        id: `evt_${(1e9 + n * 7919).toString(36)}${Math.floor(Math.random() * 36 ** 3).toString(36)}`,
        type: rand(TYPES),
        endpoint: rand(ENDPOINTS),
        status,
        latency: status === 200 ? Math.floor(4 + Math.random() * 14) : Math.floor(120 + Math.random() * 400),
        time: new Date().toLocaleTimeString("en-GB", { hour12: false }),
        attempt: 1,
    };
};

const STATUS_STYLE = {
    200: { label: "200 OK", cls: "border-emerald-500/30 bg-emerald-950/50 text-emerald-300" },
    429: { label: "429 · RETRY", cls: "border-amber-500/30 bg-amber-950/50 text-amber-300" },
    500: { label: "500", cls: "border-rose-500/30 bg-rose-950/50 text-rose-300" },
};

const METERS = [
    { path: "api.acme.dev/hooks", health: 99.99, p99: "9ms" },
    { path: "hooks.northwind.io/v2", health: 99.95, p99: "12ms" },
    { path: "ingest.helios.app", health: 100, p99: "7ms" },
];

export default function EventStream({ compact = false, testid = "event-stream" }) {
    const [events, setEvents] = useState(() =>
        Array.from({ length: compact ? 4 : 6 }, (_, i) => makeEvent(i)),
    );
    const [playing, setPlaying] = useState(true);
    const { reduced } = useMotion();
    const counter = useRef(10);

    const push = () => {
        counter.current += 1;
        setEvents((prev) => [makeEvent(counter.current), ...prev].slice(0, compact ? 4 : 6));
    };

    useEffect(() => {
        if (!playing || reduced) return undefined;
        const t = setInterval(push, 1700);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [playing, reduced, compact]);

    const retryFirst = () => {
        setEvents((prev) =>
            prev.map((e, i) =>
                i === 0 && e.status !== 200
                    ? { ...e, status: 200, attempt: e.attempt + 1, latency: Math.floor(4 + Math.random() * 10) }
                    : e,
            ),
        );
    };

    return (
        <div data-testid={testid} className="flex h-full flex-col">
            <div className="flex items-center justify-between border-b border-slate-800/80 px-4 py-2.5">
                <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-rose-500/70" />
                    <span className="h-2 w-2 rounded-full bg-amber-500/70" />
                    <span className="h-2 w-2 rounded-full bg-emerald-500/70" />
                    <span className="ml-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                        live event stream
                    </span>
                </div>
                <div className="flex items-center gap-1.5">
                    <button
                        data-testid={`${testid}-trigger-button`}
                        onClick={push}
                        title="Trigger event"
                        className="rounded border border-slate-700/80 p-1.5 text-slate-400 transition-colors hover:border-cyan-500/40 hover:text-cyan-300"
                    >
                        <Zap className="h-3 w-3" />
                    </button>
                    <button
                        data-testid={`${testid}-retry-button`}
                        onClick={retryFirst}
                        title="Retry latest failed"
                        className="rounded border border-slate-700/80 p-1.5 text-slate-400 transition-colors hover:border-amber-500/40 hover:text-amber-300"
                    >
                        <RotateCcw className="h-3 w-3" />
                    </button>
                    <button
                        data-testid={`${testid}-pause-button`}
                        onClick={() => setPlaying((v) => !v)}
                        title={playing ? "Pause" : "Play"}
                        className="rounded border border-slate-700/80 p-1.5 text-slate-400 transition-colors hover:border-cyan-500/40 hover:text-cyan-300"
                    >
                        {playing ? <Pause className="h-3 w-3" /> : <Play className="h-3 w-3" />}
                    </button>
                </div>
            </div>

            <div
                className={`space-y-1 overflow-hidden px-2 py-2 ${compact ? "h-[156px]" : "h-[228px]"}`}
            >
                <AnimatePresence initial={false} mode="popLayout">
                    {events.map((e) => {
                        const s = STATUS_STYLE[e.status];
                        return (
                            <motion.div
                                key={e.id + e.attempt}
                                initial={{ opacity: 0, y: -12 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                                data-testid={`${testid}-row`}
                                className="flex items-center justify-between gap-2 rounded-md border border-transparent px-2 py-1.5 font-mono text-[11px] hover:border-slate-800 hover:bg-slate-900/50"
                            >
                                <div className="flex min-w-0 items-center gap-2.5">
                                    <span className="hidden text-slate-600 sm:inline">{e.time}</span>
                                    <span className="truncate text-slate-300">{e.type}</span>
                                </div>
                                <div className="flex shrink-0 items-center gap-2.5">
                                    <span className="hidden max-w-[140px] truncate text-slate-500 md:inline">
                                        {e.endpoint}
                                    </span>
                                    <span className="text-slate-500">{e.latency}ms</span>
                                    <span
                                        className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold ${s.cls}`}
                                    >
                                        {s.label}
                                    </span>
                                </div>
                            </motion.div>
                        );
                    })}
                </AnimatePresence>
            </div>

            <div className="grid grid-cols-1 gap-x-6 gap-y-2 border-t border-slate-800/80 px-4 py-3 sm:grid-cols-3">
                {METERS.map((m) => (
                    <div key={m.path} className="min-w-0">
                        <div className="flex items-center justify-between font-mono text-[10px] text-slate-500">
                            <span className="truncate">{m.path}</span>
                            <span className="text-emerald-400">{m.p99}</span>
                        </div>
                        <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-slate-800">
                            <motion.div
                                initial={{ width: 0 }}
                                whileInView={{ width: `${m.health}%` }}
                                viewport={{ once: true }}
                                transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                                className="h-full rounded-full bg-gradient-to-r from-teal-500 to-cyan-400"
                            />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
